import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import { Modal, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityIcon from '@mui/icons-material/Visibility';
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Grid } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LocalAtmTwoToneIcon from "@mui/icons-material/LocalAtmTwoTone";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import AddIcon from "@mui/icons-material/Add";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import NotesIcon from '@mui/icons-material/Notes';
import { CSVLink } from 'react-csv';
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import axios from "../../../../api/axios";

const URL = "./dealership";

// ---------------- Action Components ----------------
const EditData = ({ selectedRow, navigate }) => (
  <Tooltip title="Edit">
    <EditIcon
      style={{ cursor: "pointer" }}
      onClick={(e) => {
        e.stopPropagation();
        navigate("/AddDealership", {
          state: { type: "update", value: selectedRow },
        });
      }}
    />
  </Tooltip>
);

const ManageAccount = ({ selectedRow, navigate }) => (
  <Tooltip title="Update Dealership User">
    <ManageAccountsIcon
      style={{ cursor: "pointer" }}
      onClick={(e) => {
        e.stopPropagation();
        navigate("/AddDealership", {
          state: { type: "updateUser", value: selectedRow },
        });
      }}
    />
  </Tooltip>
);

const Relationshipusers = ({ selectedRow, navigate }) => (
  <Tooltip title="Update Relationship manager">
    <MonetizationOnIcon
      style={{ cursor: "pointer" }}
      onClick={(e) => {
        e.stopPropagation();
        navigate("/RelationshipDealershipUser", {
          state: { type: "fetchUsers", value: selectedRow },
        });
      }}
    />
  </Tooltip>
);

const BlockUser = ({ selectedRow, onAction }) => (
  <Tooltip title="Block Dealership">
    <LockOpenIcon
      style={{ cursor: "pointer" }}
      onClick={() => onAction(selectedRow, "block")}
    />
  </Tooltip>
);

const UnBlockUser = ({ selectedRow, onAction }) => (
  <Tooltip title="UnBlock Dealership">
    <LockIcon
      style={{ cursor: "pointer" }}
      onClick={() => onAction(selectedRow, "unblock")}
    />
  </Tooltip>
);

const ViewNotesIcon = ({ selectedRow, handleOpenViewNotes }) => (
  <Tooltip title="View Notes">
    <NotesIcon
      style={{ cursor: "pointer", color: "#0d2365" }}
      onClick={(e) => {
        e.stopPropagation();
        handleOpenViewNotes(selectedRow);
      }}
    />
  </Tooltip>
);

const deleteIcon = ({ selectedRow, handleOpenViewNotes }) => (
  <Tooltip title="View Notes">
    <NotesIcon
      style={{ cursor: "pointer", color: "#0d2365" }}
      onClick={(e) => {
        e.stopPropagation();
        handleOpenViewNotes(selectedRow);
      }}
    />
  </Tooltip>
);

const AddNoteIcon = ({ selectedRow, handleOpenAddNote }) => (
  <Tooltip title="Add Note">
    <AddIcon
      style={{ cursor: "pointer", color: "#0d2365" }}
      onClick={(e) => {
        e.stopPropagation();
        handleOpenAddNote(selectedRow);
      }}
    />
  </Tooltip>
);

// ---------------- Main Component ----------------
function ViewDealership() {
  const navigate = useNavigate();

  // ---------------- State ----------------
  const [dataList, setDataList] = useState([]);
  const [accountName, setAccountName] = useState("");
  const [city, setCity] = useState("");

  const [refreshData, setRefreshData] = useState(false);

  // ---------------- Alert ----------------
  const [alertOpen, setAlertopen] = useState(false);
  const [severity, setSeverity] = useState("");
  const [message, setMessage] = useState("");

  // ---------------- Selected Dealer ----------------
  const [selectedDealer, setSelectedDealer] = useState(null);

  // ---------------- Rebate Modal ----------------
  const [openRebate, setOpenRebate] = useState(false);
  const [rebatePrice, setRebatePrice] = useState("");

  // ---------------- Notes Modals ----------------
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [viewNotesOpen, setViewNotesOpen] = useState(false);
  const [editNoteOpen, setEditNoteOpen] = useState(false);
  const [currentNotes, setCurrentNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [editingNote, setEditingNote] = useState(null);

  const [viewNoteOpen, setViewNoteOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [trigger, setTrigger] = useState(false); // State to trigger useEffect

  // ---------------- Load Data ----------------
  const loadData = useCallback(async () => {
    try {
      const response = await axios.get(URL);
      if (response.data?.data) {
        const dataWithIndex = response.data.data
          .filter((row) => row.accountName !== "GVC")
          .map((item, index) => ({
            slNo: index + 1,
            notes: item.notes || [],
            ...item,
          }));
        setDataList(dataWithIndex);
      } else {
        setDataList([]);
      }
    } catch (err) {
      console.error(err);
      setDataList([]);
    }
  }, []);

  useEffect(() => {
    if (trigger) {
      console.log("useEffect is triggered by the action!");
      // Perform side effects here (e.g., fetching data, updating the DOM)

      // Reset trigger to avoid continuous execution
      setTrigger(false);
    }
    loadData();
  }, [loadData, refreshData, trigger]);

  // ---------------- Alert Handlers ----------------
  const handleCloseAlert = useCallback((_, reason) => {
    if (reason !== "clickaway") setAlertopen(false);
  }, []);

  // ---------------- Rebate Modal ----------------
  const handleOpenRebate = useCallback((dealer) => {
    setSelectedDealer(dealer);
    setOpenRebate(true);
  }, []);

  const handleCloseRebate = () => setOpenRebate(false);

  const handleUpdateRebate = async () => {
    if (!rebatePrice) {
      setSeverity("error");
      setMessage("Enter rebate percentage");
      setAlertopen(true);
      return;
    }
    try {
      await axios.post("./dealership/createRebatePercent", {
        dealership_id: selectedDealer.id,
        rebate_percent: rebatePrice,
        effective_month: new Date().toISOString().slice(0, 7) + "-01",
      });
      setSeverity("success");
      setMessage("Rebate updated successfully");
      setAlertopen(true);
      setOpenRebate(false);
      setRefreshData((old) => !old);
    } catch (err) {
      console.error(err);
      setSeverity("error");
      setMessage("Failed to update rebate");
      setAlertopen(true);
    }
  };

  // ---------------- Block/Unblock ----------------
  const onAction = useCallback(async (row, action) => {
    const confirmed = window.confirm(
      `Are you sure you want to ${action === "block" ? "block" : "unblock"} this dealership?`
    );
    if (!confirmed) return;

    try {
      await axios.post("./dealership/blockDealership", {
        id: row.id,
        action: action === "block" ? 1 : 0,
      });
      setSeverity("success");
      setMessage("Action successful");
      setAlertopen(true);
      setRefreshData((old) => !old);
    } catch (err) {
      console.error(err);
      setSeverity("error");
      setMessage("Action failed");
      setAlertopen(true);
    }
  }, []);

  // ---------------- Notes CRUD ----------------
  const handleOpenAddNote = (dealer) => {
    setSelectedDealer(dealer);
    setNoteText("");
    setAddNoteOpen(true);
  };

  const handleOpenViewNotes = async (dealer) => {
    setSelectedDealer(dealer);
    try {
      const res = await axios.post(`./dealership/getDealershipNote`, { dealershipId: dealer.id });
      setCurrentNotes(res.data?.data || []);
      setViewNotesOpen(true);
    } catch (err) {
      console.error(err);
      setSeverity("error");
      setMessage("Failed to load notes");
      setAlertopen(true);
    }
  };



  const handleOpenEditNote = (note) => {
    setEditingNote(note);
    setNoteText(note.notes);
    setEditNoteOpen(true);
  };

  const handleSaveAddNote = async () => {
    if (!noteText.trim()) {
      setSeverity("error");
      setMessage("Note cannot be empty");
      setAlertopen(true);
      return;
    }
    try {
      await axios.post(`./dealership/saveDealershipNote`, { dealershipId: selectedDealer.id, notes: noteText, action: "add", id: "" });
      setSeverity("success");
      setMessage("Note added successfully");
      setAlertopen(true);
      setAddNoteOpen(false);
    } catch (err) {
      console.error(err);
      setSeverity("error");
      setMessage("Failed to add note");
      setAlertopen(true);
    }
  };

  const handleUpdateEditNote = async () => {
    if (!noteText.trim()) {
      setSeverity("error");
      setMessage("Note cannot be empty");
      setAlertopen(true);
      return;
    }
    try {
      await axios.post(`./dealership/saveDealershipNote`, { dealershipId: selectedDealer.id, notes: noteText, action: "edit", id: editingNote.id });
      setSeverity("success");
      setMessage("Note updated successfully");
      setAlertopen(true);
      setEditNoteOpen(false);
      setEditingNote(null);
      const res = await axios.post(`./dealership/getDealershipNote`, { dealershipId: selectedDealer.id });
      setCurrentNotes(res.data?.data || []);
      setViewNotesOpen(true);
    } catch (err) {
      console.error(err);
      setSeverity("error");
      setMessage("Failed to update note");
      setAlertopen(true);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.post(`./dealership/deleteDealershipNote`, { dealershipId: selectedDealer.id, notes: noteText, action: "edit", id: noteId });
      setSeverity("success");
      setMessage("Note deleted successfully");
      setAlertopen(true);
      setCurrentNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      console.error(err);
      setSeverity("error");
      setMessage("Failed to delete note");
      setAlertopen(true);
    }
  };

  const handleViewNote = (note) => {
    setSelectedNote(note);
    setViewNoteOpen(true);
  };

  const handleCloseViewNote = () => {
    setSelectedNote(null);
    setViewNoteOpen(false);
  };

  const DeleteData = (props) => {
    return (
      <Tooltip title="Delete">
        <DeleteIcon style={{ cursor: "pointer" }}
          onClick={() => {
            const isConfirmed = window.confirm("Are you sure you want to delete?");
            if (isConfirmed) {
              console.log(props.selectedRow.id);
              const data = { id: props.selectedRow.id };
              const mainURL = 'dealership/deleteDealership';
              serviceMethod(mainURL, data, handleSuccess, handleException);
            }
          }}
        />
      </Tooltip>

    );
  };

  const serviceMethod = async (mainURL, data, handleSuccess, handleException) => {

    try {
      const response = await axios.post(mainURL, data);
      return handleSuccess(response.data);

    } catch (err) {
      if (!err?.response) {
        console.log("No server response");
      } else {
        return handleException(err?.response.data);
      }
    }
  };

  const handleSuccess = (data) => {
    console.log("data response", data);
    setSeverity("success");
    setMessage(data.data);
    setAlertopen(true);
    setTimeout(() => {
      setTrigger(true);  // This will trigger useEffect
      setAlertopen(false);
    }, 3000); // Matches autoHideDuration

  }

  const handleException = (data) => {
    console.log("data error", data);
    setSeverity("error");
    setMessage(data.data);
    setAlertopen(true);
    setTimeout(() => {
      setTrigger(true);  // This will trigger useEffect
      setAlertopen(false);
    }, 3000); // Matches autoHideDuration
  }

  // ---------------- DataGrid ----------------
  const columns = useMemo(() => [
    { field: "slNo", headerName: "ID", width: 70 },
    { field: "accountName", headerName: "Account Name", width: 200 },
    { field: "accountPhone", headerName: "Phone", width: 150 },
    { field: "acc_ovmic_no", headerName: "OMVIC Number", width: 150 },
    { field: "billingCity", headerName: "City", width: 150 },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 450,
      getActions: (params) => {
        const row = params.row;
        return row.blocked === "0"
          ? [
            <EditData key="edit" selectedRow={row} navigate={navigate} />,
            <DeleteData selectedRow={params.row} />,
            <BlockUser key="block" selectedRow={row} onAction={onAction} />,
            <ManageAccount key="manage" selectedRow={row} navigate={navigate} />,
            <Relationshipusers key="rel" selectedRow={row} navigate={navigate} />,
            <Tooltip title="Rebate Percentage" key="rebate">
              <LocalAtmTwoToneIcon
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenRebate(row);
                }}
              />
            </Tooltip>,
            <AddNoteIcon key="addNote" selectedRow={row} handleOpenAddNote={handleOpenAddNote} />,
            <ViewNotesIcon key="viewNote" selectedRow={row} handleOpenViewNotes={handleOpenViewNotes} />
          ]
          : [
            <EditData key="edit" selectedRow={row} navigate={navigate} />,
            <DeleteData selectedRow={params.row} />,
            <UnBlockUser key="unblock" selectedRow={row} onAction={onAction} />,
            <ManageAccount key="manage" selectedRow={row} navigate={navigate} />,
            <Relationshipusers key="rel" selectedRow={row} navigate={navigate} />,
            <Tooltip title="Rebate Percentage" key="rebate">
              <LocalAtmTwoToneIcon
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenRebate(row);
                }}
              />
            </Tooltip>,
            <AddNoteIcon key="addNote" selectedRow={row} handleOpenAddNote={handleOpenAddNote} />,
            <ViewNotesIcon key="viewNote" selectedRow={row} handleOpenViewNotes={handleOpenViewNotes} />

          ];
      },
    },
  ], [navigate, onAction]);

  const filteredRows = useMemo(() => {
    return dataList.filter((row) => {
      const matchesAccount =
        !accountName ||
        row.accountName?.toLowerCase().includes(accountName.toLowerCase());

      const matchesCity =
        !city ||
        row.billingCity?.toLowerCase().includes(city.toLowerCase());

      return matchesAccount && matchesCity;
    });
  }, [accountName, city, dataList]);

  return (
    <Box sx={{ mt: 10, p: 2 }}>
      {/* Search + Add */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={3}>
          <TextField
            label="Search by Account Name"
            variant="outlined"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Search by City"
            variant="outlined"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={1.8} />
        <Grid item xs={4}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              color="inherit"
              onClick={() =>
                navigate("/AddDealership", {
                  state: { type: "add", value: "" },
                })
              }
              sx={{
                backgroundColor: "#0d2365",
                color: "white",
                "&:hover": { backgroundColor: "#0d2365" },
                borderRadius: "10px",
              }}
              startIcon={<AddIcon />}
            >
              Add Dealership
            </Button>

            <Button
              sx={{
                backgroundColor: "#0d2365",
                color: "white",
                "&:hover": { backgroundColor: "#0d2365" },
                borderRadius: "10px",
              }}
            >
              <CSVLink
                data={dataList}
                filename="1.csv"
                style={{
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                Export
              </CSVLink>
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* DataGrid */}
      <DataGrid
        rows={filteredRows}
        columns={columns}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        pagination
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        autoHeight
        sx={{ "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" } }}
      />

      {/* ---------------- Rebate Modal ---------------- */}
      <Modal open={openRebate} onClose={handleCloseRebate}>
        <Box sx={{ backgroundColor: "white", p: 3, borderRadius: 2, width: 350, mx: "auto", mt: "15%", display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6">Update Rebate Percentage</Typography>
          <Typography variant="subtitle2">Current: {selectedDealer?.current_rebate_percent || 0}%</Typography>
          <TextField label="Rebate Percentage" variant="outlined" value={rebatePrice} onChange={(e) => setRebatePrice(e.target.value)} fullWidth />
          <Button variant="contained" sx={{ backgroundColor: "#0d2365", color: "white" }} onClick={handleUpdateRebate}>Update</Button>
        </Box>
      </Modal>

      {/* ---------------- Add Note Modal ---------------- */}
      <Modal open={addNoteOpen} onClose={() => setAddNoteOpen(false)}>
        <Box sx={{ backgroundColor: "white", p: 3, borderRadius: 2, width: 400, mx: "auto", mt: "15%", display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6">Add Note for {selectedDealer?.accountName}</Typography>
          <TextField multiline rows={4} variant="outlined" value={noteText} onChange={(e) => setNoteText(e.target.value)} fullWidth />
          <Button variant="contained" sx={{ backgroundColor: "#0d2365", color: "white" }} onClick={handleSaveAddNote}>Add Note</Button>
        </Box>
      </Modal>

      {/* ---------------- View Notes Modal ---------------- */}
      <Modal open={viewNotesOpen} onClose={() => setViewNotesOpen(false)}>
        <Box
          sx={{
            backgroundColor: "white",
            p: 2,
            borderRadius: 2,
            width: { xs: "95%", sm: 600 },
            mx: "auto",
            mt: "10%",
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Notes for {selectedDealer?.accountName}
          </Typography>

          {currentNotes.length === 0 ? (
            <Typography>No notes yet.</Typography>
          ) : (
            <DataGrid
              rows={currentNotes.map((note, index) => ({
                id: note.id,
                slno: index + 1,
                notes: note.notes,
              }))}
              columns={[
                {
                  field: "slno",
                  headerName: "S.No",
                  width: 70,
                  align: "center",
                  headerAlign: "center",
                },
                {
                  field: "notes",
                  headerName: "Note",
                  flex: 1,
                  minWidth: 200,
                  renderCell: (params) => (
                    <Typography
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                      }}
                      title={params.value}
                    >
                      {params.value}
                    </Typography>
                  ),
                },
                {
                  field: "actions",
                  headerName: "Actions",
                  minWidth: 200,
                  sortable: false,
                  renderCell: (params) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      <Tooltip title="View Note">
                        <VisibilityIcon
                          style={{ cursor: "pointer" }}
                          onClick={() => handleViewNote(params.row)}
                        />
                      </Tooltip>
                      <Tooltip title="Update Note">
                        <EditIcon
                          style={{ cursor: "pointer" }}
                          onClick={() => handleOpenEditNote(params.row)}
                        />
                      </Tooltip>
                      <Tooltip title="Delete Note">
                        <DeleteIcon
                          style={{ cursor: "pointer" }}
                          onClick={() => handleDeleteNote(params.row)}
                        />
                      </Tooltip>


                    </Box>
                  ),
                },
              ]}
              pageSize={5}
              rowsPerPageOptions={[5, 10]}
              autoHeight
              disableSelectionOnClick
              disableColumnMenu
            />
          )}
        </Box>
      </Modal>

      <Modal open={viewNoteOpen} onClose={handleCloseViewNote}>
        <Box
          sx={{
            backgroundColor: "white",
            p: 3,
            borderRadius: 2,
            width: 400,
            mx: "auto",
            mt: "15%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="h6">Note Details</Typography>
          <Typography sx={{ wordBreak: "break-word" }}>
            {selectedNote?.notes}
          </Typography>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#0d2365", color: "white" }}
            onClick={handleCloseViewNote}
          >
            Close
          </Button>
        </Box>
      </Modal>

      {/* ---------------- Edit Note Modal ---------------- */}
      <Modal open={editNoteOpen} onClose={() => setEditNoteOpen(false)}>
        <Box sx={{ backgroundColor: "white", p: 3, borderRadius: 2, width: 400, mx: "auto", mt: "15%", display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6">Edit Note</Typography>
          <TextField multiline rows={4} variant="outlined" value={noteText} onChange={(e) => setNoteText(e.target.value)} fullWidth />
          <Button variant="contained" sx={{ backgroundColor: "#0d2365", color: "white" }} onClick={handleUpdateEditNote}>Update Note</Button>
        </Box>
      </Modal>

      {/* ---------------- Snackbar ---------------- */}
      <Snackbar open={alertOpen} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity={severity} variant="filled">{message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default ViewDealership;
